import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface Step {
  id: number;
  title: string;
  description: string;
  component: React.ReactNode;
}

const InteractiveTroubleshooting: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);

  const steps: Step[] = [
    {
      id: 1,
      title: 'Locate the PoE Power Injector',
      description: 'Find the small PoE box, typically located 4-5 feet from your router or near where the cable enters your home.',
      component: <PoELocatorGraphic />
    },
    {
      id: 2,
      title: 'Check PoE Connections',
      description: 'Ensure all cables are firmly plugged in and the power adapter is connected.',
      component: <PoEConnectionsGraphic />
    },
    {
      id: 3,
      title: 'Check the LAN Cable to Router',
      description: 'Follow the LAN cable from the PoE injector to your router.',
      component: <LANCableGraphic />
    },
    {
      id: 4,
      title: 'Verify the Router Port',
      description: 'Make sure the cable is connected to the WAN or Internet port on your router.',
      component: <RouterPortGraphic />
    },
    {
      id: 5,
      title: 'Power Cycle Your Router',
      description: 'As a final step, restart your router to refresh the connection.',
      component: <RouterPowerCycleGraphic />
    }
  ];

  const keepHeaderVisible = () => {
    if (!headerRef.current) return;

    // On mobile the header is sticky under the nav — avoid scrollIntoView
    // (it was yanking the % bar and step numbers up off-screen).
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    const rect = headerRef.current.getBoundingClientRect();
    const navOffset = 80;
    if (rect.top < navOffset) {
      const top = window.scrollY + rect.top - navOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setTimeout(keepHeaderVisible, 50);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setTimeout(keepHeaderVisible, 50);
    }
  };

  const goToStep = (index: number) => {
    setCurrentStep(index);
    setTimeout(keepHeaderVisible, 50);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-visible md:overflow-hidden">
      {/* Progress + step numbers — sticky on mobile so Next/Prev never hide them */}
      <div
        ref={headerRef}
        className="sticky top-16 z-20 bg-white border-b border-gray-200 shadow-sm md:static md:shadow-none md:border-b-0 scroll-mt-16"
      >
        {/* Progress Bar */}
        <div className="bg-gray-100 px-4 md:px-8 py-3 md:py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm text-gray-600">
              {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2">
            <motion.div
              className="bg-airmax-blue h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Step Indicators — compact dots on mobile */}
        <div className="px-4 md:px-8 py-3 md:py-6 border-b border-gray-200 md:border-b">
          <div className="flex justify-between items-center gap-1 md:gap-0">
            {steps.map((step, index) => (
              <button
                key={step.id}
                type="button"
                onClick={() => goToStep(index)}
                className={`flex flex-col items-center flex-1 ${
                  index !== steps.length - 1 ? 'md:border-r md:border-gray-200' : ''
                }`}
              >
                <div
                  className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center mb-0 md:mb-2 transition-all ${
                    index === currentStep
                      ? 'bg-airmax-blue text-white scale-110'
                      : index < currentStep
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index < currentStep ? (
                    <CheckCircleIcon className="w-5 h-5 md:w-6 md:h-6" />
                  ) : (
                    <span className="font-bold text-sm md:text-base">{index + 1}</span>
                  )}
                </div>
                <span
                  className={`text-xs text-center px-2 hidden md:block ${
                    index === currentStep ? 'text-airmax-blue font-semibold' : 'text-gray-600'
                  }`}
                >
                  {step.title.split(' ').slice(0, 3).join(' ')}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 md:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-4">
              {steps[currentStep].title}
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600 mb-4 md:mb-8">
              {steps[currentStep].description}
            </p>

            {/* Visual Component */}
            <div className="bg-gray-50 rounded-xl p-3 md:p-8 mb-4 md:mb-8 max-h-[42vh] md:max-h-none overflow-auto">
              {steps[currentStep].component}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation — sticky on mobile so Next is always reachable */}
      <div className="bg-gray-50 px-4 md:px-8 py-4 md:py-6 flex justify-between items-center gap-3 sticky bottom-0 md:static border-t border-gray-200">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className={`flex items-center px-4 md:px-6 py-3 rounded-lg font-semibold transition-all ${
            currentStep === 0
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white text-airmax-blue border-2 border-airmax-blue hover:bg-airmax-blue hover:text-white'
          }`}
        >
          <ChevronLeftIcon className="w-5 h-5 md:mr-2" />
          <span className="hidden md:inline">Previous</span>
        </button>

        <button
          onClick={nextStep}
          disabled={currentStep === steps.length - 1}
          className={`flex items-center px-4 md:px-6 py-3 rounded-lg font-semibold transition-all ${
            currentStep === steps.length - 1
              ? 'bg-green-500 text-white'
              : 'bg-airmax-blue text-white hover:bg-blue-700'
          }`}
        >
          {currentStep === steps.length - 1 ? (
            <>
              <CheckCircleIcon className="w-5 h-5 md:mr-2" />
              <span className="hidden sm:inline">Complete</span>
            </>
          ) : (
            <>
              <span className="hidden md:inline">Next</span>
              <span className="md:hidden">Next step</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Step 1: PoE Locator Graphic
const PoELocatorGraphic: React.FC = () => {
  return (
    <div className="relative w-full max-w-3xl mx-auto">
      {/* Background */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
        
        {/* Label at top */}
        <motion.div
          className="mb-2 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="inline-block bg-white rounded-lg shadow-lg px-4 md:px-6 py-2 md:py-3 border-2 border-blue-500">
            <h3 className="text-lg md:text-2xl font-bold text-blue-600">PoE Power Injector</h3>
          </div>
        </motion.div>

        {/* Distance indicator */}
        <motion.div
          className="mb-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="inline-block bg-yellow-100 rounded-full px-4 md:px-6 py-1.5 md:py-2 border-2 border-yellow-400">
            <p className="text-sm md:text-base text-gray-700 font-semibold">📏 Usually 4-5 feet from your router</p>
          </div>
        </motion.div>

        {/* PoE Image with annotations */}
        <div className="relative">
          {/* Pulsing glow effect */}
          <motion.div
            className="absolute inset-0 bg-blue-400 rounded-lg blur-xl"
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          
          {/* Actual PoE Image */}
          <motion.img
            src={`${process.env.PUBLIC_URL}/images/poe.png`}
            alt="PoE Power Injector"
            className="relative w-full max-w-md mx-auto rounded-lg shadow-2xl"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
          />
        </div>

        {/* Info box */}
        <motion.div
          className="mt-4 md:mt-6 bg-white rounded-xl shadow-lg p-4 md:p-6 border-2 border-blue-400"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
        >
          <p className="text-base md:text-lg text-blue-600 font-bold text-center mb-2">
            Look for a small box with two ethernet ports
          </p>
          <p className="text-sm md:text-base text-gray-600 text-center">
            Usually located near where the cable enters your home or close to your router
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// Step 2: PoE Connections Graphic
const PoEConnectionsGraphic: React.FC = () => {
  return (
    <div className="relative w-full max-w-3xl mx-auto">
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
        
        {/* PoE and Cables Image */}
        <div className="relative mb-8">
          <motion.img
            src={`${process.env.PUBLIC_URL}/images/poeandcables.png`}
            alt="PoE Power Injector with Cables"
            className="w-full max-w-2xl mx-auto rounded-lg shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
          />
        </div>

        {/* Connection Checklist */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4">
          {/* Cable from Antenna */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-2 md:p-3 border-2 border-green-500"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center mb-1">
              <div className="w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                <span className="text-white text-xs md:text-sm">✓</span>
              </div>
              <h4 className="font-bold text-gray-900 text-xs md:text-sm">POE Port</h4>
            </div>
            <p className="text-xs md:text-xs text-gray-600">Cable from antenna firmly connected</p>
          </motion.div>

          {/* Cable to Router */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-2 md:p-3 border-2 border-green-500"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <div className="flex items-center mb-1">
              <div className="w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                <span className="text-white text-xs md:text-sm">✓</span>
              </div>
              <h4 className="font-bold text-gray-900 text-xs md:text-sm">LAN Port</h4>
            </div>
            <p className="text-xs md:text-xs text-gray-600">Cable to router firmly connected</p>
          </motion.div>

          {/* Power Adapter */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-2 md:p-3 border-2 border-green-500"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
          >
            <div className="flex items-center mb-1">
              <div className="w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                <span className="text-white text-xs md:text-sm">✓</span>
              </div>
              <h4 className="font-bold text-gray-900 text-xs md:text-sm">Power</h4>
            </div>
            <p className="text-xs md:text-xs text-gray-600">Check LED blink for power and check power cable</p>
          </motion.div>
        </div>

        {/* Warning about reset button */}
        <motion.div
          className="bg-red-50 rounded-lg shadow-md p-2 md:p-3 border-2 border-red-500 mb-3 md:mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
        >
          <p className="text-red-700 font-bold text-center text-xs md:text-sm">
            ⚠️ NEVER push the reset button on the PoE!
          </p>
        </motion.div>

        {/* Success Message */}
        <motion.div
          className="bg-green-100 rounded-xl shadow-lg p-4 md:p-6 border-2 border-green-500 mb-3 md:mb-4"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2 }}
        >
          <p className="text-base md:text-xl text-green-700 font-bold text-center">
            ✓ All Connections Secure
          </p>
        </motion.div>

        {/* Info box */}
        <motion.div
          className="bg-blue-50 rounded-xl shadow-lg p-3 md:p-4 border-2 border-blue-400"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5 }}
        >
          <p className="text-sm md:text-base text-blue-700 font-semibold text-center">
            Make sure all cables are firmly connected and power adapter is plugged in
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// Step 3: LAN Cable Graphic
const LANCableGraphic: React.FC = () => {
  return (
    <div className="relative w-full max-w-3xl mx-auto">
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
        
        {/* LAN to Router Image */}
        <div className="relative mb-8">
          <motion.img
            src={`${process.env.PUBLIC_URL}/images/lantorouter.png`}
            alt="LAN Cable from PoE to Router"
            className="w-full max-w-2xl mx-auto rounded-lg shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
          />
        </div>

        {/* Label with background */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-4 md:p-6 border-2 border-blue-500 mb-3 md:mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <p className="text-lg md:text-2xl text-blue-600 font-bold text-center mb-2">
            Follow the LAN cable to your router
          </p>
          <p className="text-sm md:text-base lg:text-lg text-gray-600 text-center">
            Usually a blue or yellow ethernet cable
          </p>
        </motion.div>

        {/* Info box */}
        <motion.div
          className="bg-blue-50 rounded-xl shadow-lg p-3 md:p-5 border-2 border-blue-400"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
        >
          <p className="text-sm md:text-base lg:text-lg text-blue-700 font-semibold text-center">
            💡 The cable connects from the PoE LAN port to your router's WAN port
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// Step 4: Router Port Graphic
const RouterPortGraphic: React.FC = () => {
  return (
    <div className="relative w-full max-w-3xl mx-auto">
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
        
        {/* Main Instruction */}
        <motion.div
          className="bg-green-100 rounded-xl shadow-lg p-4 md:p-6 border-2 border-green-500 mb-4 md:mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-lg md:text-2xl text-green-700 font-bold text-center">
            ✓ Connect ONLY to WAN/Internet Port
          </p>
        </motion.div>

        {/* Router Image */}
        <div className="relative mb-8">
          <motion.img
            src={`${process.env.PUBLIC_URL}/images/router.png`}
            alt="Router WAN Port Connection"
            className="w-full max-w-2xl mx-auto rounded-lg shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
          />
        </div>

        {/* Warning about LAN ports */}
        <motion.div
          className="bg-red-50 rounded-lg shadow-md p-2 md:p-3 border-2 border-red-500 mb-2 md:mb-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
        >
          <p className="text-red-700 font-bold text-center text-xs md:text-sm mb-1">
            ❌ DO NOT USE LAN PORTS
          </p>
          <p className="text-red-600 text-center text-xs">
            Using LAN ports instead of WAN will NOT work
          </p>
        </motion.div>

        {/* Warning about reset button */}
        <motion.div
          className="bg-red-50 rounded-lg shadow-md p-2 md:p-3 border-2 border-red-500 mb-2 md:mb-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
        >
          <p className="text-red-700 font-bold text-center text-xs md:text-sm">
            ⚠️ NEVER push the reset button on the router!
          </p>
        </motion.div>

        {/* Critical Warning Box */}
        <motion.div
          className="bg-red-100 rounded-lg shadow-md p-3 md:p-4 border-2 border-red-600 mb-2 md:mb-3"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2 }}
        >
          <p className="text-red-800 font-bold text-center text-xs md:text-sm mb-1">
            ⚠️ IMPORTANT: Using LAN ports instead of WAN will NOT work!
          </p>
          <p className="text-red-700 text-center text-xs">
            The cable from your PoE must connect to the WAN/Internet port only
          </p>
        </motion.div>

        {/* Info note */}
        <motion.div
          className="bg-blue-50 rounded-lg shadow-md p-2 md:p-3 border-2 border-blue-400"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5 }}
        >
          <p className="text-blue-700 text-center font-semibold text-xs md:text-sm">
            💡 WAN port may also be labeled "Internet" or be a different color
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// Step 5: Router Power Cycle Graphic
const RouterPowerCycleGraphic: React.FC = () => {
  return (
    <div className="relative w-full max-w-3xl mx-auto">
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
        
        {/* Router Image */}
        <div className="relative mb-8">
          <motion.img
            src={`${process.env.PUBLIC_URL}/images/router2026.png`}
            alt="Router Power Cycle"
            className="w-full max-w-2xl mx-auto rounded-lg shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
          />
        </div>

        {/* Power Cycle Instructions */}
        <div className="space-y-4">
          {/* Step 1: Unplug */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-4 border-2 border-blue-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm md:text-base mb-1">Unplug the Power Cable</h4>
                <p className="text-xs md:text-sm text-gray-600">Disconnect the power cable from your router</p>
              </div>
            </div>
          </motion.div>

          {/* Step 2: Wait */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-4 border-2 border-yellow-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1 }}
          >
            <div className="flex items-start">
              <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm md:text-base mb-1">Wait 3 Seconds</h4>
                <p className="text-xs md:text-sm text-gray-600">Count to 3 before reconnecting</p>
              </div>
            </div>
          </motion.div>

          {/* Step 3: Reconnect */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-4 border-2 border-green-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5 }}
          >
            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm md:text-base mb-1">Reconnect Power Cable</h4>
                <p className="text-xs md:text-sm text-gray-600">Plug the power cable back into the router</p>
              </div>
            </div>
          </motion.div>

          {/* Step 4: Wait for Boot */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-4 border-2 border-blue-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 2 }}
          >
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-white font-bold">4</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm md:text-base mb-1">Wait 1 Minute</h4>
                <p className="text-xs md:text-sm text-gray-600">Allow the router to fully restart and boot up</p>
              </div>
            </div>
          </motion.div>

          {/* Step 5: Check Lights */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-4 border-2 border-orange-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 2.5 }}
          >
            <div className="flex items-start">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-white font-bold">5</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm md:text-base mb-1">Check All Lights Are Back On</h4>
                <p className="text-xs md:text-sm text-gray-600">Verify that all indicator lights on the router are lit and stable</p>
              </div>
            </div>
          </motion.div>

          {/* Step 6: Test Internet */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-4 border-2 border-teal-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 3 }}
          >
            <div className="flex items-start">
              <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-white font-bold">6</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm md:text-base mb-1">Check Your Internet Connection</h4>
                <p className="text-xs md:text-sm text-gray-600">Try opening a website on your phone or computer to confirm the connection is working</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Success Message */}
        <motion.div
          className="mt-6 bg-green-50 rounded-lg shadow-md p-4 border-2 border-green-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.5 }}
        >
          <p className="text-green-700 font-bold text-center text-sm md:text-base">
            ✅ If your internet is working, you're all set! If not, please contact support.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default InteractiveTroubleshooting;
